document.addEventListener('alpine:init', () => {
  Alpine.data('CtrPurchaseBoxSkio', (sectionId) => ({
    sectionId,
    product: null,
    selectedVariant: null,
    variantMetafieldsById: {},
    sellingPlans: [],
    selectedSellingPlanId: null,
    selectedSellingPlan: null,
    typePurchase: 'one-time',
    sellingPlansDisabled: false,
    includeAdditionalDiscount: false,
    additionalDiscountText: '',
    isAddingToCart: false,
    qty: 1,

    incrementQty() {
      this.qty += 1;
    },

    decrementQty() {
      if (this.qty > 1) this.qty -= 1;
    },

    debug(label, payload) {
      console.log(`[ctr-isolate-pdb][skio][${this.sectionId}] ${label}`, payload);
    },

    getAllocationPlanId(allocation) {
      if (!allocation) return null;

      const id =
        allocation.selling_plan_id ||
        allocation.selling_plan?.id ||
        allocation.selling_plan?.selling_plan_id ||
        null;

      return id ? String(id) : null;
    },

    logStateSnapshot(label) {
      this.debug(label, {
        typePurchase: this.typePurchase,
        selectedVariant: this.selectedVariant,
        selectedVariantId: this.selectedVariant?.id || null,
        selectedVariantTitle: this.selectedVariant?.title || null,
        selectedVariantAllocations: this.selectedVariant?.selling_plan_allocations || [],
        sellingPlans: this.sellingPlans,
        availablePlansForVariant: this.getAvailablePlansForVariant(),
        selectedSellingPlan: this.selectedSellingPlan,
        selectedSellingPlanId: this.selectedSellingPlanId,
        discountPercentage: this.getDiscountPercentage(),
        discountedPrice: this.getDiscountedPrice(),
      });
    },

    init() {
      this.includeAdditionalDiscount = this.$el.dataset.includeAdditionalDiscount === 'true';
      this.additionalDiscountText = this.$el.dataset.additionalDiscountText || '';
      this.sellingPlansDisabled = this.$el.dataset.disableSellingPlans === 'true';
      this.debug('init:start', {
        sellingPlansDisabled: this.sellingPlansDisabled,
        includeAdditionalDiscount: this.includeAdditionalDiscount,
        additionalDiscountText: this.additionalDiscountText,
      });
      this.parseProductData();
      this.setupSellingPlans();

      window.addEventListener('ctr-isolate-purchase-box:variant-change', (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        this.debug('event:variant-change', event.detail);
        this.product = event.detail.product;
        this.selectedVariant = this.enrichVariant(event.detail.variant);
        this.setupSellingPlans();
        this.syncInputs();
        this.refreshSkioCopy();
        this.logStateSnapshot('snapshot:after-variant-change');
      });

      this.syncInputs();
      this.refreshSkioCopy();
      this.logStateSnapshot('snapshot:after-init');
      this.debug('init:complete', {
        product: this.product,
        selectedVariant: this.selectedVariant,
        sellingPlans: this.sellingPlans,
        selectedSellingPlanId: this.selectedSellingPlanId,
        typePurchase: this.typePurchase,
      });
    },

    /**
     * Re-resolve the [skio_*] tokens in merchant copy after a variant or plan
     * change. Liquid already rendered them for first paint, so this only has to
     * run when the numbers move.
     */
    refreshSkioCopy() {
      if (!this.selectedVariant) return;
      const section = document.querySelector(
        `[data-ctr-isolate-purchase-box-section="${this.sectionId}"]`
      );
      const utils = window.CtrSkioPlans;
      utils.applyCopyTokens(
        section || document,
        utils.copyValues(this.selectedVariant, this.selectedSellingPlan)
      );
    },

    parseProductData() {
      const section = document.querySelector(`[data-ctr-isolate-purchase-box-section="${this.sectionId}"]`);
      if (!section) return;

      const jsonNode = section.querySelector('[data-ctr-isolate-purchase-box-product-json]');
      const sellingPlansNode = section.querySelector('[data-ctr-isolate-purchase-box-selling-plans]');
      const variantMetafieldsNode = section.querySelector('[x-ref="productVariantsMetafieldJson"]');
      if (!jsonNode) return;

      try {
        const metafields = variantMetafieldsNode ? JSON.parse(variantMetafieldsNode.textContent) : [];
        this.variantMetafieldsById = Object.fromEntries(
          (metafields || []).map((entry) => [String(entry.id), entry.metafields || {}])
        );
        this.debug('parseProductData:variant-metafields-map', this.variantMetafieldsById);
      } catch (error) {
        console.error('ctr-isolate-purchase-box: failed to parse variant metafields JSON for skio', error);
        this.variantMetafieldsById = {};
      }

      try {
        this.product = JSON.parse(jsonNode.textContent);
        const initialVariant =
          this.product.variants?.find(
            (variant) => String(variant.id) === String(this.product.selected_or_first_available_variant_id)
          ) ||
          this.product.variants?.find((variant) => variant.available) ||
          this.product.variants?.[0] ||
          null;
        this.selectedVariant = this.enrichVariant(initialVariant);
        this.debug('parseProductData:product-json', this.product);
        this.debug('parseProductData:selectedVariant', this.selectedVariant);
      } catch (error) {
        console.error('ctr-isolate-purchase-box: failed to parse product JSON for skio', error);
      }

      try {
        this.sellingPlanGroups = sellingPlansNode ? JSON.parse(sellingPlansNode.textContent) : [];
        this.debug('parseProductData:selling-plan-groups-json', this.sellingPlanGroups);
      } catch (error) {
        console.error('ctr-isolate-purchase-box: failed to parse selling plan groups JSON', error);
        this.sellingPlanGroups = [];
      }
    },

    enrichVariant(variant) {
      if (!variant) return null;

      return {
        ...variant,
        metafields: this.variantMetafieldsById[String(variant.id)] || variant.metafields || {},
      };
    },

    setupSellingPlans() {
      const groups = this.sellingPlanGroups?.length ? this.sellingPlanGroups : this.product?.selling_plan_groups || [];
      this.debug('setupSellingPlans:groups', groups);
      this.sellingPlans = window.CtrSkioPlans.collectPlans({ selling_plan_groups: groups });
      this.debug('setupSellingPlans:plans', this.sellingPlans);

      if (this.sellingPlansDisabled) {
        this.sellingPlans = [];
        this.selectedSellingPlan = null;
        this.selectedSellingPlanId = null;
        this.typePurchase = 'one-time';
        this.debug('setupSellingPlans:disabled', {
          sellingPlansDisabled: this.sellingPlansDisabled,
        });
        return;
      }

      this.typePurchase = this.sellingPlans.length ? 'subscribe' : 'one-time';
      this.setSellingPlanForVariant(this.selectedVariant);
      this.debug('setupSellingPlans:result', {
        sellingPlans: this.sellingPlans,
        typePurchase: this.typePurchase,
        selectedSellingPlan: this.selectedSellingPlan,
        selectedSellingPlanId: this.selectedSellingPlanId,
        selectedVariantAllocations: this.selectedVariant?.selling_plan_allocations || [],
      });
    },

    setSellingPlanForVariant(variant) {
      if (!variant || !this.sellingPlans.length) {
        this.selectedSellingPlan = null;
        this.selectedSellingPlanId = null;
        this.debug('setSellingPlanForVariant:no-variant-or-plans', {
          variant,
          sellingPlans: this.sellingPlans,
        });
        return;
      }

      const allocationIds = (variant.selling_plan_allocations || [])
        .map((allocation) => String(this.getAllocationPlanId(allocation) || ''))
        .filter(Boolean);
      const availablePlans = this.sellingPlans.filter((plan) => allocationIds.includes(String(plan.id)));
      const matchedPlan = availablePlans[0] || null;
      this.selectedSellingPlan = matchedPlan || null;
      this.selectedSellingPlanId = matchedPlan?.id || null;
      this.debug('setSellingPlanForVariant:result', {
        variant,
        allocationIds,
        availablePlans,
        matchedPlan,
        selectedSellingPlan: this.selectedSellingPlan,
        selectedSellingPlanId: this.selectedSellingPlanId,
        selectedVariantAllocations: variant.selling_plan_allocations || [],
      });
    },

    getAvailablePlansForVariant() {
      if (!this.selectedVariant) return [];
      const allocationIds = (this.selectedVariant.selling_plan_allocations || [])
        .map((allocation) => String(this.getAllocationPlanId(allocation) || ''))
        .filter(Boolean);
      const availablePlans = this.sellingPlans.filter((plan) => allocationIds.includes(String(plan.id)));
      this.debug('getAvailablePlansForVariant', {
        selectedVariantId: this.selectedVariant?.id,
        allocationIds,
        sellingPlans: this.sellingPlans,
        availablePlans,
      });
      return availablePlans;
    },

    changeTypePurchase(type) {
      if (this.sellingPlansDisabled && type === 'subscribe') {
        this.debug('changeTypePurchase:subscribe-blocked', {
          sellingPlansDisabled: this.sellingPlansDisabled,
        });
        return;
      }
      this.typePurchase = type;
      window.dispatchEvent(
        new CustomEvent('ctr-isolate-purchase-box:purchase-type-change', {
          detail: {
            sectionId: this.sectionId,
            typePurchase: this.typePurchase,
          },
        })
      );
      this.syncInputs();
    },

    onChangeSellingPlan(event) {
      const id = String(event.target.value);
      const availablePlans = this.getAvailablePlansForVariant();
      this.selectedSellingPlan = availablePlans.find((plan) => String(plan.id) === id) || null;
      this.selectedSellingPlanId = this.selectedSellingPlan?.id || null;
      this.debug('onChangeSellingPlan', {
        selectedValue: id,
        availablePlans,
        selectedSellingPlan: this.selectedSellingPlan,
        selectedSellingPlanId: this.selectedSellingPlanId,
      });
      this.syncInputs();
    },

    syncInputs() {
      if (!this.$refs.form) return;

      const variantInput = this.$refs.form.querySelector('.product-variant-id');
      const sellingPlanInput = this.$refs.form.querySelector('.product-selling-plan-id');

      if (variantInput && this.selectedVariant?.id) {
        variantInput.value = this.selectedVariant.id;
      }

      if (sellingPlanInput) {
        sellingPlanInput.value =
          this.typePurchase === 'subscribe' && this.selectedSellingPlanId ? this.selectedSellingPlanId : '';
      }
      this.debug('syncInputs', {
        variantInputValue: variantInput?.value,
        sellingPlanInputValue: sellingPlanInput?.value,
        typePurchase: this.typePurchase,
      });
    },

    getPriceMoney(cents) {
      if (typeof cents !== 'number') return '';
      return `$${(cents / 100).toFixed(2)}`;
    },

    getDiscountedPrice() {
      if (!this.selectedVariant) {
        this.debug('getDiscountedPrice:no-selected-variant', {});
        return '';
      }
      if (this.typePurchase !== 'subscribe' || !this.selectedSellingPlan) {
        const fallbackPrice = this.getPriceMoney(this.selectedVariant.price);
        this.debug('getDiscountedPrice:fallback-base-price', {
          typePurchase: this.typePurchase,
          selectedSellingPlan: this.selectedSellingPlan,
          selectedVariantPrice: this.selectedVariant.price,
          fallbackPrice,
        });
        return fallbackPrice;
      }

      const subscribePrice = window.CtrSkioPlans.unitSubscribePrice(
        this.selectedVariant,
        this.selectedSellingPlan
      );
      this.debug('getDiscountedPrice:allocation-price', {
        selectedSellingPlan: this.selectedSellingPlan,
        selectedVariantPrice: this.selectedVariant.price,
        result: subscribePrice,
      });
      return this.getPriceMoney(subscribePrice);
    },

    getSubscriptionPrice() {
      if (!this.selectedVariant) {
        this.debug('getSubscriptionPrice:no-selected-variant', {});
        return '';
      }

      if (!this.selectedSellingPlan) {
        const fallbackPrice = this.getPriceMoney(this.selectedVariant.price);
        this.debug('getSubscriptionPrice:no-selected-plan', {
          selectedVariantPrice: this.selectedVariant.price,
          fallbackPrice,
        });
        return fallbackPrice;
      }

      const subscribePrice = window.CtrSkioPlans.unitSubscribePrice(
        this.selectedVariant,
        this.selectedSellingPlan
      );
      this.debug('getSubscriptionPrice:allocation-price', {
        selectedSellingPlan: this.selectedSellingPlan,
        selectedVariantPrice: this.selectedVariant.price,
        result: subscribePrice,
      });
      return this.getPriceMoney(subscribePrice);
    },

    getDiscountPercentage() {
      if (!this.selectedVariant || !this.selectedSellingPlan) {
        this.debug('getDiscountPercentage:no-plan', {
          selectedVariant: this.selectedVariant,
          selectedSellingPlan: this.selectedSellingPlan,
        });
        return 0;
      }

      const result = window.CtrSkioPlans.discountPercentage(
        this.selectedVariant,
        this.selectedSellingPlan
      );
      this.debug('getDiscountPercentage:result', {
        selectedSellingPlan: this.selectedSellingPlan,
        selectedVariantPrice: this.selectedVariant.price,
        result,
      });
      return result;
    },

    getCompareAtPrice() {
      if (!this.selectedVariant) return '';
      return this.getPriceMoney(this.selectedVariant.compare_at_price || 0);
    },

    getVariantAllocationIds() {
      return (this.selectedVariant?.selling_plan_allocations || [])
        .map((allocation) => this.getAllocationPlanId(allocation))
        .filter(Boolean);
    },

    getAvailablePlanIds() {
      return this.getAvailablePlansForVariant().map((plan) => String(plan.id));
    },

    getSelectedSellingPlanLabel() {
      if (!this.selectedSellingPlan) return 'none';
      return `${this.selectedSellingPlan.name} (${this.selectedSellingPlan.id})`;
    },

    getProductDebugJson() {
      try {
        return JSON.stringify(this.product || {}, null, 2);
      } catch (error) {
        return `failed to stringify product: ${error?.message || error}`;
      }
    },

    getSelectedVariantDebugJson() {
      try {
        return JSON.stringify(this.selectedVariant || {}, null, 2);
      } catch (error) {
        return `failed to stringify variant: ${error?.message || error}`;
      }
    },

    async addToCart(redirectToCheckout = false) {
      if (!this.selectedVariant?.id || !this.$refs.form) return;

      const item = {
        id: this.selectedVariant.id,
        quantity: this.qty,
      };

      if (this.typePurchase === 'subscribe' && this.selectedSellingPlanId) {
        const availablePlans = this.getAvailablePlansForVariant();
        const validPlan =
          availablePlans.find((plan) => String(plan.id) === String(this.selectedSellingPlanId)) ||
          availablePlans[0] ||
          null;

        if (validPlan) {
          item.selling_plan = validPlan.id;
          this.selectedSellingPlan = validPlan;
          this.selectedSellingPlanId = validPlan.id;
        } else {
          this.debug('addToCart:no-valid-selling-plan-for-variant', {
            selectedVariant: this.selectedVariant,
            selectedSellingPlanId: this.selectedSellingPlanId,
            availablePlans,
          });
        }

        if (this.includeAdditionalDiscount && this.additionalDiscountText) {
          item.properties = {
            ...(item.properties || {}),
            _offer: this.additionalDiscountText,
          };
        }
      }

      const freeGiftInputs = Array.from(this.$refs.form.querySelectorAll('.product-free-product-id'));
      const items = [item];

      freeGiftInputs.forEach((input) => {
        const variantId = Number(input.dataset.additionalProductId);
        if (!variantId) return;

        items.push({
          id: variantId,
          quantity: 1,
          parent_id: this.selectedVariant.id,
          properties: {
            _offer: input.value || 'bonus',
            _is_bonus: 'true',
          },
        });
      });

      this.isAddingToCart = true;
      this.debug('addToCart:payload', {
        redirectToCheckout,
        selectedVariant: this.selectedVariant,
        selectedSellingPlan: this.selectedSellingPlan,
        selectedSellingPlanId: this.selectedSellingPlanId,
        typePurchase: this.typePurchase,
        items,
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          throw new Error(`Failed to add to cart: ${response.status}`);
        }

        if (redirectToCheckout) {
          window.location.href = '/checkout';
          return;
        }

        if (window.Alpine?.store('cart')?.refreshCart) {
          Alpine.store('cart').refreshCart();
        } else {
          window.location.href = '/cart';
        }
      } catch (error) {
        console.error('ctr-isolate-purchase-box: add to cart failed', error);
      } finally {
        this.isAddingToCart = false;
      }
    },
  }));
});
